import { localDateString } from '../../../../../utils/helpers/localDate';
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from '@iconify/react';
import { getMeterSlabsByHead, createMeterSlab, updateMeterSlab, deleteMeterSlab } from "../../../../../redux/actions/SocietyPortal/MeterSlabAction/MeterSlabAction";
import FormInput from "../../../../../components/child/FormInput";
import FormSelect from "../../../../../components/child/FormSelect";
import FormButton from "../../../../../components/child/FormButton";

const MeterSlabModal = ({ show, handleClose, chargeHead }) => {
    const dispatch = useDispatch();
    const { meterSlabsData, loading, createLoading, updateLoading, deleteLoading } = useSelector(state => state.MeterSlabReducer);

    const [showAddForm, setShowAddForm] = useState(false);
    const [editingSlab, setEditingSlab] = useState(null);
    const [formData, setFormData] = useState({
        slab_type: '',
        slab_from: '',
        slab_to: '',
        rate_per_unit: '',
        fixed_charge: '',
        effective_from: localDateString(),
        effective_to: '',
        display_order: 1
    });

    // Slab types
    const slabTypes = [
        { value: 'FIXED', label: 'Fixed (Same rate for all units)' },
        { value: 'LESS', label: 'Less Than (0 to X units)' },
        { value: 'GREATER', label: 'Greater Than (Above X units)' },
        { value: 'RANGE', label: 'Range (X to Y units)' }
    ];

    // Check if FIXED slab exists
    const hasFixedSlab = meterSlabsData?.some(s => s.slab_type === 'FIXED' && s.status === 1);

    // Filter available slab types based on existing slabs
    const getAvailableSlabTypes = () => {
        if (hasFixedSlab) {
            // If FIXED exists, no other types can be added
            return [];
        }
        // If non-FIXED slabs exist, FIXED cannot be added
        const hasOtherSlabs = meterSlabsData?.some(s => s.slab_type !== 'FIXED' && s.status === 1);
        if (hasOtherSlabs) {
            return slabTypes.filter(t => t.value !== 'FIXED');
        }
        return slabTypes;
    };

    // Fetch slabs when modal opens
    useEffect(() => {
        if (show && chargeHead?.head_id) {
            dispatch(getMeterSlabsByHead({ head_id: chargeHead.head_id, include_inactive: false }));
        }
    }, [show, chargeHead, dispatch]);

    // Reset form when closing
    useEffect(() => {
        if (!show) {
            resetForm();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const resetForm = () => {
        setShowAddForm(false);
        setEditingSlab(null);
        setFormData({
            slab_type: '',
            slab_from: '',
            slab_to: '',
            rate_per_unit: '',
            fixed_charge: '',
            effective_from: localDateString(),
            effective_to: '',
            display_order: (meterSlabsData?.length || 0) + 1
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSlabTypeChange = (e) => {
        const slabType = e.target.value;
        setFormData(prev => ({
            ...prev,
            slab_type: slabType,
            slab_from: slabType === 'LESS' || slabType === 'FIXED' ? 0 : '',
            slab_to: slabType === 'GREATER' || slabType === 'FIXED' ? '' : prev.slab_to
        }));
    };

    const validateForm = () => {
        if (!formData.slab_type) return 'Please select a slab type';
        if (!formData.rate_per_unit && formData.rate_per_unit !== 0) return 'Rate per unit is required';

        switch (formData.slab_type) {
            case 'LESS':
                if (!formData.slab_to && formData.slab_to !== 0) return 'Slab To is required for LESS type';
                break;
            case 'GREATER':
                if (!formData.slab_from && formData.slab_from !== 0) return 'Slab From is required for GREATER type';
                break;
            case 'RANGE':
                if (!formData.slab_from && formData.slab_from !== 0) return 'Slab From is required for RANGE type';
                if (!formData.slab_to && formData.slab_to !== 0) return 'Slab To is required for RANGE type';
                if (parseFloat(formData.slab_from) >= parseFloat(formData.slab_to)) return 'Slab From must be less than Slab To';
                break;
            default:
                break;
        }

        // Effective To is optional, but when set it must be after Effective From
        if (formData.effective_to && new Date(formData.effective_to) <= new Date(formData.effective_from)) {
            return 'Effective To date must be greater than the Effective From date';
        }
        return null;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const error = validateForm();
        if (error) {
            alert(error);
            return;
        }

        const payload = {
            head_id: chargeHead.head_id,
            slab_type: formData.slab_type,
            slab_from: formData.slab_type === 'LESS' ? 0 : (parseFloat(formData.slab_from) || 0),
            slab_to: formData.slab_type === 'GREATER' ? null : (parseFloat(formData.slab_to) || null),
            rate_per_unit: parseFloat(formData.rate_per_unit) || 0,
            fixed_charge: parseFloat(formData.fixed_charge) || 0,
            effective_from: formData.effective_from || null,
            effective_to: formData.effective_to || null,
            display_order: parseInt(formData.display_order) || 1,
            onSuccess: () => {
                resetForm();
                dispatch(getMeterSlabsByHead({ head_id: chargeHead.head_id, include_inactive: false }));
            }
        };

        if (editingSlab) {
            dispatch(updateMeterSlab({ id: editingSlab.slab_id, ...payload }));
        } else {
            dispatch(createMeterSlab(payload));
        }
    };

    const handleEdit = (slab) => {
        setEditingSlab(slab);
        setFormData({
            slab_type: slab.slab_type,
            slab_from: slab.slab_from ?? '',
            slab_to: slab.slab_to ?? '',
            rate_per_unit: slab.rate_per_unit ?? '',
            fixed_charge: slab.fixed_charge ?? '',
            effective_from: slab.effective_from ? slab.effective_from.split('T')[0] : '',
            effective_to: slab.effective_to ? slab.effective_to.split('T')[0] : '',
            display_order: slab.display_order || 1
        });
        setShowAddForm(true);
    };

    const handleDelete = (slabId) => {
        if (window.confirm('Are you sure you want to delete this slab?')) {
            dispatch(deleteMeterSlab({
                id: slabId,
                onSuccess: () => {
                    dispatch(getMeterSlabsByHead({ head_id: chargeHead.head_id, include_inactive: false }));
                }
            }));
        }
    };

    const formatSlabRange = (slab) => {
        switch (slab.slab_type) {
            case 'FIXED': return 'All units';
            case 'LESS': return `0 - ${slab.slab_to} units`;
            case 'GREATER': return `Above ${slab.slab_from} units`;
            case 'RANGE': return `${slab.slab_from} - ${slab.slab_to} units`;
            default: return '-';
        }
    };

    const getSlabTypeColor = (type) => {
        switch (type) {
            case 'FIXED': return { bg: '#dcfce7', color: '#16a34a' };
            case 'LESS': return { bg: '#e0e7ff', color: '#4f46e5' };
            case 'GREATER': return { bg: '#fef3c7', color: '#d97706' };
            case 'RANGE': return { bg: '#fce7f3', color: '#db2777' };
            default: return { bg: '#f3f4f6', color: '#6b7280' };
        }
    };

    const activeSlabs = meterSlabsData?.filter(s => s.status === 1) || [];
    const availableSlabTypes = getAvailableSlabTypes();

    if (!show) return null;

    return (
        <div className="modal-backdrop-custom" onClick={handleClose}>
            <div className="modal-content-custom card radius-16 border-0 shadow-lg" style={{ maxWidth: '800px' }} onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="card-header py-10 px-16 border-bottom d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                        <div className="w-44-px h-44-px rounded-circle bg-warning-100 d-flex align-items-center justify-content-center">
                            <Icon icon="mdi:chart-timeline-variant" className="text-warning-600 text-2xl" />
                        </div>
                        <div>
                            <h5 className="fw-semibold mb-1">Meter Slabs</h5>
                            <p className="text-secondary-light text-sm mb-0">{chargeHead?.head_name} ({chargeHead?.head_code})</p>
                        </div>
                    </div>
                    <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
                </div>

                {/* Modal Body */}
                <div className="card-body p-24">
                    {/* Info Banner */}
                    {hasFixedSlab && (
                        <div className="alert alert-info d-flex align-items-center gap-2 mb-20">
                            <Icon icon="mdi:information" className="text-xl" />
                            <span className="text-sm">FIXED slab is configured. Same rate applies to all consumption units.</span>
                        </div>
                    )}

                    {/* Slab List */}
                    {loading ? (
                        <div className="d-flex justify-content-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : activeSlabs.length > 0 ? (
                        <div className="table-responsive mb-20">
                            <table className="table table-hover mb-0">
                                <thead className="bg-neutral-50">
                                    <tr>
                                        <th className="py-8 px-12 text-secondary-light text-sm fw-semibold">#</th>
                                        <th className="py-8 px-12 text-secondary-light text-sm fw-semibold">Type</th>
                                        <th className="py-8 px-12 text-secondary-light text-sm fw-semibold">Range</th>
                                        <th className="py-8 px-12 text-secondary-light text-sm fw-semibold text-end">Rate/Unit</th>
                                        <th className="py-8 px-12 text-secondary-light text-sm fw-semibold text-end">Fixed Charge</th>
                                        <th className="py-8 px-12 text-secondary-light text-sm fw-semibold text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activeSlabs.map((slab, index) => {
                                        const typeColor = getSlabTypeColor(slab.slab_type);
                                        return (
                                            <tr key={slab.slab_id}>
                                                <td className="py-8 px-12 align-middle">
                                                    <span className="text-secondary-light">{index + 1}</span>
                                                </td>
                                                <td className="py-8 px-12 align-middle">
                                                    <span
                                                        className="badge px-10 py-6 radius-4 fw-medium"
                                                        style={{ backgroundColor: typeColor.bg, color: typeColor.color }}
                                                    >
                                                        {slab.slab_type}
                                                    </span>
                                                </td>
                                                <td className="py-8 px-12 align-middle">
                                                    <span className="fw-medium">{formatSlabRange(slab)}</span>
                                                </td>
                                                <td className="py-8 px-12 align-middle text-end">
                                                    <span className="fw-semibold text-primary-600">₹{parseFloat(slab.rate_per_unit).toFixed(2)}</span>
                                                </td>
                                                <td className="py-8 px-12 align-middle text-end">
                                                    <span className="text-secondary-light">₹{parseFloat(slab.fixed_charge || 0).toFixed(2)}</span>
                                                </td>
                                                <td className="py-8 px-12 align-middle text-center">
                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary-50 text-primary-600 p-1"
                                                            onClick={() => handleEdit(slab)}
                                                            title="Edit"
                                                        >
                                                            <Icon icon="mdi:pencil" className="text-lg" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger-50 text-danger-600 p-1"
                                                            onClick={() => handleDelete(slab.slab_id)}
                                                            disabled={deleteLoading}
                                                            title="Delete"
                                                        >
                                                            <Icon icon="mdi:delete" className="text-lg" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-4 mb-20">
                            <Icon icon="mdi:chart-timeline-variant" className="text-secondary-light text-4xl mb-2" style={{ opacity: 0.3 }} />
                            <p className="text-secondary-light text-sm mb-0">No slabs configured yet</p>
                        </div>
                    )}

                    {/* Add/Edit Form */}
                    {showAddForm ? (
                        <div className="border radius-12 p-20 bg-neutral-50">
                            <div className="d-flex align-items-center justify-content-between mb-16">
                                <h6 className="fw-semibold mb-0">{editingSlab ? 'Edit Slab' : 'Add New Slab'}</h6>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={resetForm}
                                ></button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    {/* Slab Type */}
                                    <div className="col-md-6">
                                        <FormSelect
                                            label="Slab Type"
                                            name="slab_type"
                                            value={formData.slab_type}
                                            onChange={handleSlabTypeChange}
                                            options={editingSlab ? slabTypes : availableSlabTypes}
                                            placeholder="-- Select Type --"
                                            required
                                            disabled={editingSlab}
                                        />
                                    </div>

                                    {/* Display Order */}
                                    <div className="col-md-6">
                                        <FormInput
                                            label="Display Order"
                                            type="number"
                                            name="display_order"
                                            value={formData.display_order}
                                            onChange={handleInputChange}
                                            min="1"
                                        />
                                    </div>

                                    {/* Slab From - Show for GREATER and RANGE */}
                                    {(formData.slab_type === 'GREATER' || formData.slab_type === 'RANGE') && (
                                        <div className="col-md-6">
                                            <FormInput
                                                label="Slab From (Units)"
                                                type="number"
                                                name="slab_from"
                                                value={formData.slab_from}
                                                onChange={handleInputChange}
                                                placeholder="e.g., 100"
                                                min="0"
                                                step="0.01"
                                                required
                                            />
                                        </div>
                                    )}

                                    {/* Slab To - Show for LESS and RANGE */}
                                    {(formData.slab_type === 'LESS' || formData.slab_type === 'RANGE') && (
                                        <div className="col-md-6">
                                            <FormInput
                                                label="Slab To (Units)"
                                                type="number"
                                                name="slab_to"
                                                value={formData.slab_to}
                                                onChange={handleInputChange}
                                                placeholder="e.g., 200"
                                                min="0"
                                                step="0.01"
                                                required
                                            />
                                        </div>
                                    )}

                                    {/* Rate Per Unit */}
                                    <div className="col-md-6">
                                        <FormInput
                                            label="Rate Per Unit (₹)"
                                            type="number"
                                            name="rate_per_unit"
                                            value={formData.rate_per_unit}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 5.00"
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                    </div>

                                    {/* Fixed Charge */}
                                    <div className="col-md-6">
                                        <FormInput
                                            label="Fixed Charge (₹)"
                                            type="number"
                                            name="fixed_charge"
                                            value={formData.fixed_charge}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 50.00"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>

                                    {/* Effective From */}
                                    <div className="col-md-6">
                                        <FormInput
                                            label="Effective From"
                                            type="date"
                                            name="effective_from"
                                            value={formData.effective_from}
                                            onChange={handleInputChange}
                                        />
                                    </div>

                                    {/* Effective To */}
                                    <div className="col-md-6">
                                        <FormInput
                                            label="Effective To (Optional)"
                                            type="date"
                                            name="effective_to"
                                            value={formData.effective_to}
                                            onChange={handleInputChange}
                                            min={formData.effective_from || undefined}
                                        />
                                    </div>
                                </div>

                                {/* Form Actions */}
                                <div className="d-flex justify-content-end gap-2 mt-20">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-20"
                                        onClick={resetForm}
                                    >
                                        Cancel
                                    </button>
                                    <FormButton
                                        type="submit"
                                        variant="primary"
                                        isLoading={createLoading || updateLoading}
                                    >
                                        {editingSlab ? 'Update Slab' : 'Add Slab'}
                                    </FormButton>
                                </div>
                            </form>
                        </div>
                    ) : (
                        /* Add Button - Only show if can add more slabs */
                        availableSlabTypes.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-primary d-flex align-items-center gap-2 w-100 justify-content-center py-12"
                                onClick={() => setShowAddForm(true)}
                            >
                                <Icon icon="mdi:plus-circle" className="text-xl" />
                                Add New Slab
                            </button>
                        )
                    )}

                    {/* Warning if no more slabs can be added */}
                    {!showAddForm && availableSlabTypes.length === 0 && !hasFixedSlab && activeSlabs.length > 0 && (
                        <div className="alert alert-warning d-flex align-items-center gap-2 mt-16 mb-0">
                            <Icon icon="mdi:alert-circle" className="text-xl" />
                            <span className="text-sm">Cannot add FIXED slab when other slab types exist.</span>
                        </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="card-footer py-10 px-16 border-top d-flex justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-secondary px-24"
                        onClick={handleClose}
                    >
                        Close
                    </button>
                </div>
            </div>

            {/* Custom Styles */}
            <style>{`
                .modal-backdrop-custom {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1050;
                    animation: fadeIn 0.2s ease;
                }

                .modal-content-custom {
                    width: 100%;
                    margin: 16px;
                    animation: slideUp 0.3s ease;
                    max-height: 90vh;
                    overflow-y: auto;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .w-44-px { width: 44px; }
                .h-44-px { height: 44px; }
            `}</style>
        </div>
    );
};

export default MeterSlabModal;
